import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Card, CardActions, CardContent, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { campaignsPathTo, discountsPathTo } from '../utils/Api';
import SnackbarContext from '../contexts/SnackbarContext';
import { currency } from './building_blocks/Currency';

const formatDateTime = (value) => {
  if (!value) return '-';

  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusLabel = {
  draft: 'Rascunho',
  campaign_scheduled: 'Inscrita na campanha',
  active: 'Ativo',
  ended: 'Encerrado',
};

const statusColor = {
  draft: 'default',
  campaign_scheduled: 'info',
  active: 'success',
  ended: 'warning',
};

const ProviderDiscountList = () => {
  const [discounts, setDiscounts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { openSnackbar, openConfirmDialog } = useContext(SnackbarContext);

  const loadDiscounts = () => {
    setLoading(true);
    Promise.all([discountsPathTo.list(), campaignsPathTo.list({ enrollment_open: true })])
      .then(([discountsResult, campaignsResult]) => {
        setDiscounts(discountsResult.data);
        setCampaigns(campaignsResult.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadDiscounts();
  }, []);

  const handleActivate = (discount) => {
    openConfirmDialog(
      'Ativar esta promoção? Apenas uma promoção pode ficar ativa por vez.',
      async () => {
        try {
          const result = await discountsPathTo.activate(discount.id);
          setDiscounts(discounts.map((item) => (item.id === discount.id ? result.data : item)));
          openSnackbar('Promoção ativada com sucesso!', 'success');
        } catch (error) {
          openSnackbar(error?.response?.data?.errors?.[0] || 'Não foi possível ativar a promoção.', 'error');
        }
      }
    );
  };

  const handleDeactivate = async (discount) => {
    try {
      const result = await discountsPathTo.deactivate(discount.id);
      setDiscounts(discounts.map((item) => (item.id === discount.id ? result.data : item)));
      openSnackbar('Promoção desativada com sucesso!', 'success');
    } catch (error) {
      openSnackbar(error?.response?.data?.errors?.[0] || 'Não foi possível desativar a promoção.', 'error');
    }
  };

  const handleDelete = (discount) => {
    openConfirmDialog(
      'Remover esta promoção? Esta ação é irreversível.',
      async () => {
        try {
          await discountsPathTo.delete(discount.id);
          setDiscounts(discounts.filter((item) => item.id !== discount.id));
          openSnackbar('Promoção removida com sucesso!', 'success');
        } catch (error) {
          openSnackbar(error?.response?.data?.errors?.[0] || 'Não foi possível remover a promoção.', 'error');
        }
      }
    );
  };

  const handleJoinCampaign = (discount) => {
    const campaignId = selectedCampaigns[discount.id];
    if (!campaignId) {
      openSnackbar('Selecione uma campanha para participar.', 'error');
      return;
    }

    openConfirmDialog(
      'Participar desta campanha? As datas da promoção serão substituídas pelas datas da campanha. Se a campanha já estiver ativa, a promoção entra ativa imediatamente.',
      async () => {
        try {
          const result = await discountsPathTo.joinCampaign(discount.id, campaignId);
          setDiscounts(discounts.map((item) => (item.id === discount.id ? result.data : item)));
          openSnackbar('Promoção inscrita na campanha com sucesso!', 'success');
        } catch (error) {
          openSnackbar(error?.response?.data?.errors?.[0] || 'Não foi possível participar da campanha.', 'error');
        }
      }
    );
  };

  const handleLeaveCampaign = (discount) => {
    openConfirmDialog(
      'Desvincular esta promoção da campanha? Ela voltará para rascunho e as datas da campanha serão removidas.',
      async () => {
        try {
          const result = await discountsPathTo.leaveCampaign(discount.id);
          setDiscounts(discounts.map((item) => (item.id === discount.id ? result.data : item)));
          openSnackbar('Promoção desvinculada da campanha com sucesso!', 'success');
        } catch (error) {
          openSnackbar(error?.response?.data?.errors?.[0] || 'Não foi possível desvincular a promoção da campanha.', 'error');
        }
      }
    );
  };

  if (loading) {
    return <Typography>Carregando promoções...</Typography>;
  }

  return (
    <Stack spacing={2}>
      <Box display="flex" justifyContent="flex-end">
        <Button variant="contained" onClick={() => navigate('/provider/discounts/new')}>
          Criar promoção
        </Button>
      </Box>

      {discounts.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Nenhuma promoção criada ainda.
        </Typography>
      )}

      {discounts.map((discount) => (
        <Card key={discount.id} variant="outlined">
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
              <Typography variant="h6">{discount.name}</Typography>
              <Chip
                label={statusLabel[discount.status] || discount.status}
                color={statusColor[discount.status] || 'default'}
                size="small"
              />
            </Box>

            <Typography variant="body2" color="text.secondary">
              {formatDateTime(discount.starts_at)} até {formatDateTime(discount.ends_at)}
            </Typography>

            <Typography variant="body2" mt={1}>
              {discount.discount_items.length} produto(s) na promoção
            </Typography>

            {discount.campaign && (
              <Typography variant="body2" color="primary" mt={1}>
                Campanha: {discount.campaign.name}
              </Typography>
            )}

            {discount.discount_items.slice(0, 3).map((item) => (
              <Typography key={item.id} variant="body2" color="text.secondary">
                {item.product?.title || `Produto #${item.product_id}`}: {currency(item.sale_price)}
              </Typography>
            ))}
          </CardContent>

          <CardActions sx={{ justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={() => navigate(`/provider/discounts/${discount.id}`)}>
              Editar
            </Button>
            {discount.status === 'active' ? (
              <Button variant="outlined" color="warning" onClick={() => handleDeactivate(discount)}>
                Desativar
              </Button>
            ) : discount.status !== 'campaign_scheduled' && (
              <Button variant="contained" onClick={() => handleActivate(discount)}>
                Ativar
              </Button>
            )}
            {discount.campaign_id && discount.status !== 'ended' && (
              <Button variant="outlined" color="warning" onClick={() => handleLeaveCampaign(discount)}>
                Sair da campanha
              </Button>
            )}
            {discount.status !== 'active' && (
              <Button variant="outlined" color="error" onClick={() => handleDelete(discount)}>
                Remover
              </Button>
            )}
          </CardActions>

          {!discount.campaign_id && campaigns.length > 0 && (
            <Box display="flex" gap={1} sx={{ px: 1, pb: 1 }}>
              <TextField
                select
                size="small"
                label="Campanha"
                value={selectedCampaigns[discount.id] || ''}
                onChange={(event) => setSelectedCampaigns({ ...selectedCampaigns, [discount.id]: event.target.value })}
                fullWidth
              >
                {campaigns.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </MenuItem>
                ))}
              </TextField>
              <Button variant="contained" onClick={() => handleJoinCampaign(discount)}>
                Participar
              </Button>
            </Box>
          )}
        </Card>
      ))}
    </Stack>
  );
};

export default ProviderDiscountList;
